import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Checkout = ({ cartItems, clearCart }) => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [customerEmail, setCustomerEmail] = useState(() => localStorage.getItem('brainwave_customer_email') || '');
  const [customerName, setCustomerName] = useState(() => localStorage.getItem('brainwave_customer_name') || '');

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = subtotal >= 10000 ? 0 : 50;
  const tax = subtotal * 0.15; // 15% VAT for South Africa
  const total = subtotal + shipping + tax;

  useEffect(() => {
    // Check if returning from Stripe
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPaymentStatus = async (sessionId) => {
    setCheckingStatus(true);
    let attempts = 0;
    const maxAttempts = 5;

    const pollStatus = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          toast.success('Payment successful! Thank you for your order.', {
            duration: 4000,
            style: { backgroundColor: '#00CED1', color: 'white' }
          });
          const paidEmail = response.data.customer_email || customerEmail || '';
          const orderRef = (sessionId || '').slice(-8);
          clearCart();
          setTimeout(() => {
            if (paidEmail) {
              navigate(`/track-order?email=${encodeURIComponent(paidEmail)}&ref=${orderRef}`);
            } else {
              navigate('/shop');
            }
          }, 2000);
          return;
        } else if (response.data.status === 'expired') {
          toast.error('Payment session expired. Please try again.');
          setCheckingStatus(false);
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, 2000);
        } else {
          toast.error('Payment verification timed out. Please contact support.');
          setCheckingStatus(false);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        toast.error('Error verifying payment. Please contact support.');
        setCheckingStatus(false);
      }
    };

    pollStatus();
  };

  const handleStripeCheckout = async () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    if (!customerEmail || !customerEmail.includes('@')) {
      toast.error('Please enter a valid email so we can keep you updated on your order');
      return;
    }

    // Persist for next time
    localStorage.setItem('brainwave_customer_email', customerEmail);
    if (customerName) localStorage.setItem('brainwave_customer_name', customerName);

    setIsProcessing(true);

    try {
      const currentUrl = window.location.origin;
      const successUrl = `${currentUrl}/checkout?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${currentUrl}/cart`;

      const response = await axios.post(`${BACKEND_URL}/api/checkout/create`, {
        items: cartItems,
        success_url: successUrl,
        cancel_url: cancelUrl,
        customer_email: customerEmail,
        customer_name: customerName || null,
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to initiate checkout. Please try again.');
      setIsProcessing(false);
    }
  };

  if (cartItems.length === 0 && !checkingStatus) {
    navigate('/cart');
    return null;
  }

  if (checkingStatus) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-16 w-16 mx-auto mb-4 animate-spin" style={{ color: '#00CED1' }} />
            <h2 className="text-2xl font-bold mb-2">Processing Payment...</h2>
            <p className="text-gray-600">Please wait while we verify your payment.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <h1 className="text-4xl md:text-5xl font-bold mb-8">
          <span style={{ color: '#00CED1' }}>Checkout</span>
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div>
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 mb-6">
                  {cartItems.map((item) => (
                    <div key={item.id} className="flex justify-between items-center">
                      <div className="flex items-center space-x-3">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-16 h-16 object-cover rounded"
                        />
                        <div>
                          <p className="font-semibold">{item.name}</p>
                          <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                        </div>
                      </div>
                      <span className="font-semibold">R{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span>R{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping</span>
                    <span>{shipping === 0 ? 'FREE' : `R${shipping.toFixed(2)}`}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>VAT (15%)</span>
                    <span>R{tax.toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between items-center">
                    <span className="text-xl font-bold">Total</span>
                    <span className="text-3xl font-bold" style={{ color: '#00CED1' }}>
                      R{total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Payment Section */}
          <div>
            <Card className="border-0 shadow-lg mb-4">
              <CardHeader>
                <CardTitle>Your Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label htmlFor="checkout-name" className="text-sm font-medium">Full Name (optional)</Label>
                  <Input
                    id="checkout-name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Your name"
                    className="mt-1"
                    data-testid="checkout-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="checkout-email" className="text-sm font-medium">Email Address *</Label>
                  <Input
                    id="checkout-email"
                    type="email"
                    required
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-1"
                    data-testid="checkout-email-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    We'll email you order updates (shipped, delivered, or cancelled).
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Payment Method</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div 
                    className="border-2 rounded-lg p-6 text-center cursor-pointer hover:shadow-lg transition-all duration-300"
                    style={{ borderColor: '#00CED1' }}
                  >
                    <CreditCard className="h-12 w-12 mx-auto mb-4" style={{ color: '#00CED1' }} />
                    <h3 className="text-lg font-bold mb-2">Pay with Stripe</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Secure payment processing with credit/debit card
                    </p>
                    <Button
                      onClick={handleStripeCheckout}
                      disabled={isProcessing || !customerEmail}
                      size="lg"
                      className="w-full text-white font-semibold hover:scale-105 transition-transform duration-300 disabled:opacity-50 disabled:hover:scale-100"
                      style={{ backgroundColor: '#00CED1' }}
                      data-testid="checkout-pay-btn"
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="mr-2 h-5 w-5" />
                          Pay R{total.toFixed(2)}
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="text-center text-sm text-gray-500">
                    <p>🔒 Your payment information is secure</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button
              onClick={() => navigate('/cart')}
              variant="outline"
              className="w-full mt-4 font-semibold"
              style={{ borderColor: '#00CED1', color: '#00CED1' }}
            >
              Back to Cart
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
